import request from '../utils/request';

/** 登记抽帧推送任务 */
export function createDatasetExtract(payload) {
  return request.post('/dataset/extract', payload);
}

/** 批量登记抽帧推送任务（多目录一次提交，批次名自动取各文件夹名） */
export function createDatasetExtractBatch(payload) {
  return request.post('/dataset/extract/batch', payload);
}

/** 查任务状态（前端轮询用） */
export function fetchDatasetJob(id) {
  return request.get(`/dataset/extract/${id}`);
}

/** 标注平台项目列表 */
export function fetchLabelStudioProjects() {
  return request.get('/dataset/projects');
}

/** 目录抽帧预览（胶片条数据：pb 清单 + 每 pb 一张 front 关键帧） */
export function fetchDatasetPreview(objectName) {
  return request.get('/dataset/preview', { params: { object_name: objectName } });
}

/** 新建标注项目（后端带上标签模板并配好图源存储） */
export function createDatasetProject(title) {
  return request.post('/dataset/projects', { title });
}

/** 批次内部验收（只改验收状态，不影响版本发布） */
export function acceptDatasetBatch(batch, datasetId) {
  return request.post('/dataset/batches/acceptance', { batch, dataset_id: datasetId });
}

/** 图级元数据记录（按图一行）：batch/prefix/split/annotate_state/版本区间筛选 + 分页 */
export function fetchDatasetImages(params = {}) {
  return request.get('/dataset/images', { params });
}

/** 选图预览：抽帧任务圈定的待选图片（camera 筛选 / 分页 / ids_only=1 取整份 id） */
export function fetchDatasetSelection(jobId, params = {}) {
  return request.get(`/dataset/extract/${jobId}/selection`, { params });
}

/** 确认选图：excluded_ids 为未保留的图片，后端登记导入任务并排除这些图 */
export function confirmDatasetSelection(jobId, excludedIds = []) {
  return request.post(`/dataset/extract/${jobId}/selection`, { excluded_ids: excludedIds });
}

/**
 * 重试预标注：先修任务号缺口再登记新任务。
 * scope=gap 只补无标注；scope=all 全量重跑（覆盖旧预测）；use_registry=1 用最新类别注册表
 * （默认沿用本批快照）。
 */
export function retryDatasetPreannotate(payload) {
  return request.post('/dataset/preannotate/retry', payload);
}

/**
 * 全量批次汇总（发布候选池用）：图数/字节/最近拍摄 + 最新推送 job + LS 项目链接 + 所属版本。
 * 数据列表页的行级摘要不走这里，随 /api/data/groups 按当前页内嵌下发。
 */
export function fetchDatasetImagesStats(datasetId) {
  return request.get('/dataset/images/stats', { params: { dataset_id: datasetId } });
}

/** 数据集名册（新的在前 + 默认数据集标识）；不传 dataset_id 时全链路都按默认数据集走 */
export function fetchDatasets() {
  return request.get('/dataset/datasets');
}

/** 新建数据集：slug 唯一（重名回 409），name 可空；建的是空数据集，批次随后按它归集 */
export function createDataset(payload) {
  return request.post('/dataset/datasets', payload);
}

/** 发布前类别预览：只传版本/批次/数据集，返回 class_map / classes.txt / data.yaml / class_plan_hash */
export function previewDatasetClassPlan(payload = {}) {
  return request.post('/dataset/versions/class-plan/preview', {
    version: payload.version,
    batches: payload.batches,
    remove_batches: payload.remove_batches,
    dataset_id: payload.dataset_id,
  });
}

/** 发布版本：预检通过后登记发布任务，秒回 {job_id, version}；进度用 fetchDatasetJob 轮询 */
export function publishDatasetVersion(payload) {
  return request.post('/dataset/versions', payload);
}

/** 最近一条发布任务：重开弹窗/刷新页面找回发布进度与失败原因（无记录回 null） */
export function fetchLatestPublishJob(datasetId) {
  return request.get('/dataset/publish/latest', { params: { dataset_id: datasetId } });
}

/** 版本列表（区间累计：加入/剔除按版本合并，含训练使用情况与版本内项目名） */
export function fetchDatasetVersions(datasetId) {
  return request.get('/dataset/versions', { params: { dataset_id: datasetId } });
}

/** 软隐藏版本：只从列表/训练入口移除，不删版本、shard、label 或缓存 */
export function hideDatasetVersion(version, datasetId, reason = '') {
  return request.post(`/dataset/versions/${version}/hide`, { dataset_id: datasetId, reason });
}

/** 批量预标注可选模型：内置 SAM3 + 登记的训练模型（只列可批量预标注的） */
export function fetchPreannotateModels() {
  return request.get('/dataset/preannotate/models');
}

/** 标注项目的标签清单（模型类别与项目标签对齐校验用） */
export function fetchDatasetProjectLabels(projectId) {
  return request.get(`/dataset/projects/${projectId}/labels`);
}

/** 类别注册表（?enabled=1 只要启用的；预标注/导出/项目模板都读它） */
export function fetchDatasetClasses(params = {}) {
  return request.get('/dataset/classes', { params });
}

/** 新增类别（只追加：name 必填且不可重名，sort_order 自动排到最后） */
export function createDatasetClass(payload) {
  return request.post('/dataset/classes', payload);
}

/** 改类别（只可改 prompt / color / business_group / enabled，名字不可变） */
export function updateDatasetClass(id, patch) {
  return request.patch(`/dataset/classes/${id}`, patch);
}

/** 把注册表启用类并进存量标注项目的标签配置（只增不删） */
export function syncDatasetProjectLabelConfig(projectId) {
  return request.post(`/dataset/projects/${projectId}/sync-label-config`);
}

/** 读窗口：单图（图 + 同名标注 txt）预签名下载地址，末尾 expires_in 秒内有效 */
export function fetchDatasetImageDownloadUrl(id) {
  return request.get(`/dataset/images/${id}/download-url`);
}

/** 读窗口：批次"图 + 标注 txt"预签名清单（前端拼 zip 用） */
export function fetchDatasetBatchDownloadManifest(batch) {
  return request.get('/dataset/images/download-manifest', { params: { batch } });
}

/** 读窗口：版本整版"图 + 标注 txt"预签名清单（前端拼 zip 用） */
export function fetchDatasetVersionDownloadManifest(version, datasetId, options = {}) {
  return request.get(`/dataset/versions/${version}/download-manifest`, {
    params: { dataset_id: datasetId },
    ...options,
  });
}

/** 催办：提交批次状态后台刷新任务，立即返回 jobId（不阻塞等待 LS 查询） */
export function refreshDatasetImages(batch) {
  return request.post('/dataset/images/refresh', { batch });
}

/** 查询后台刷新任务进度（前端轮询用） */
export function fetchDatasetRefreshJob(jobId) {
  return request.get(`/dataset/images/refresh/${jobId}`);
}

/** 数据登记干跑校验：只读，回配对计数、txt 红线、解码抽检与在册类别 */
export function previewDatasetRegister(payload) {
  return request.post('/dataset/register/preview', payload);
}

/** 数据登记：建登记任务（进度复用 fetchDatasetJob 轮询） */
export function createDatasetRegister(payload) {
  return request.post('/dataset/register', payload);
}

/** 页面上传的直传链接：只给批次名时前缀由平台按当天日期生成，一次最多 200 个文件 */
export function fetchDatasetRegisterUploadUrl(payload) {
  return request.post('/dataset/register/upload-url', payload);
}

/** 目录下的批次历史（含旧批与 -rN 衍生批次） */
export function fetchDatasetBatchList(params = {}) {
  return request.get('/dataset/batches', { params });
}

/** 批次可执行动作与计数（按钮启停 / 确认文案用） */
export function fetchDatasetBatchInspect(batch, params = {}) {
  return request.get(`/dataset/batches/${encodeURIComponent(batch)}/inspect`, { params });
}

/** 重新选图：本批全部图回到待选，任务与标注保留；确认导入时再做差集 */
export function reselectDatasetBatch(batch, payload = {}) {
  return request.post(`/dataset/batches/${encodeURIComponent(batch)}/reselect`, payload);
}

/** 注入失败后是否还有可复用的推理结果（决定是否显示“重试注入”） */
export function fetchPreannotateInjectable(batch) {
  return request.get(`/dataset/batches/${encodeURIComponent(batch)}/preannotate/injectable`, { timeout: 8000 });
}

/** 只补注入：复用服务端保留的推理结果，不重新推理 */
export function injectDatasetPreannotate(batch) {
  return request.post(`/dataset/batches/${encodeURIComponent(batch)}/preannotate/inject`, {}, { timeout: 30000 });
}

/** 开始预标注：导入完成后由人显式触发，参数在此时确定 */
export function startDatasetPreannotate(batch, payload = {}) {
  return request.post(`/dataset/batches/${encodeURIComponent(batch)}/preannotate`, payload, { timeout: 120000 });
}

/** 重新生成：沿用本批参数另存新批次（-rN），旧批不动（不再支持同名覆盖） */
export function regenerateDatasetBatch(batch, payload = {}) {
  return request.post(`/dataset/batches/${encodeURIComponent(batch)}/regenerate`, payload, { timeout: 300000 });
}

/** 删除批次（图片、标注平台任务与对象存储文件一并清理） */
export function removeDatasetBatch(batch, payload = {}) {
  return request.delete(`/dataset/batches/${encodeURIComponent(batch)}`, { data: payload, timeout: 300000 });
}
